import { type Sources, ExperienceType } from '@atlassian/conversation-assistant';
import { getAnalyticsWebClient } from '../utils/analytics';
import { getProductIdentifierAttributes } from '../utils/analytics/get-product-identifier-attributes';
import { fetchJson } from './fetch-json';

export const RATE_LIMITED_STATUS_CODE = 429;

// eslint-disable-next-line no-shadow
export enum FeedbackType {
    POSITIVE = 'positive',
    NEGATIVE = 'negative',
}

export interface LoadingState {
    readonly keywords?: string[];
    readonly sources?: Sources;
}

// eslint-disable-next-line no-shadow
export enum ASSISTANT_MESSAGE_STATUS {
    IDLE = 'idle',
    LOADING_RESPONSE = 'loadingResponse',
    AWAITING_RESPONSE = 'awaitingResponse',
    TYPING_RESPONSE = 'typingResponse',
}

export interface MessageMetadata {
    isFeedbackRequired?: boolean;
    messageLatency?: number;
    messageId?: string;
    messageFeedback?: {
        feedbackLabel: string;
        detailedFeedback?: string;
        feedbackType: FeedbackType;
    };
    [key: string]: unknown;
    providerAuthDetail?: ProviderAuthDetail;
    isFallbackMessage?: boolean;
    loadingState?: LoadingState;
    status?: ASSISTANT_MESSAGE_STATUS;
}

export interface HelpSeekerMessage {
    readonly userType: 'helpseeker';
    readonly messageContentAdf: string;
    readonly userAccountId?: string | null;
    readonly messageMetadata: MessageMetadata;
}

export interface BotMessage {
    readonly userType: 'bot';
    readonly messageContentAdf: string;
    readonly userAccountId?: string | null;
    readonly messageSuggestedUrls?: string[];
    readonly confidenceScore?: number;
    readonly messageMetadata: MessageMetadata;
}

export type ChatMessage = (BotMessage | HelpSeekerMessage) & { readonly confidenceScore?: number };

export interface ProviderAuthDetail {
    readonly unauthorizedProvider: string;
    readonly authUrl: string;
}

export interface ConverseRequest {
    readonly conversationId: string;
    readonly messages: readonly ChatMessage[];
}

export interface ChatUpdates {
    readonly messages: readonly ChatMessage[];
    readonly status?: number;
    readonly providerAuthDetails?: ProviderAuthDetail[];
}

export const converse = async ({
    conversationId,
    cloudId,
    messages,
    searchAll,
    usePipeline,
}: {
    readonly cloudId: string;
    readonly conversationId: string;
    readonly messages: readonly ChatMessage[];
    readonly searchAll: boolean;
    readonly usePipeline?: string;
}): Promise<ChatUpdates> => {
    const analyticsClient = getAnalyticsWebClient();
    const converseRequest: ConverseRequest = {
        conversationId,
        messages,
    };

    const startTime = performance.now();
    const chatUpdates = await fetchJson<ChatUpdates>(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `/gateway/api/jsm/help/spike/cloudid/${cloudId}/v1/chat/converse?searchAll=${searchAll}&usePipeline=${usePipeline}`,
        'POST',
        {
            body: JSON.stringify(converseRequest),
        }
    ).catch(({ status }) => {
        if (status === RATE_LIMITED_STATUS_CODE) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            analyticsClient.sendTrackEvent({
                source: 'uhConverseRequest',
                action: 'rateLimited',
                actionSubject: 'conversation',
                actionSubjectId: conversationId,
                attributes: {
                    ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
                },
            });
            return Promise.resolve({
                // Suppressing existing violation. Please fix this.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                status,
                messages: [],
                providersAuthDetails: [],
            }) as Promise<ChatUpdates>;
        }
        return Promise.resolve({ messages: [] }) as Promise<ChatUpdates>;
    });

    const finishTime = performance.now();
    const messageLatency = finishTime - startTime;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    analyticsClient.sendTrackEvent({
        source: 'uhConverseRequest',
        action: 'fetched',
        actionSubject: 'conversation',
        actionSubjectId: conversationId,
        attributes: {
            messageLatency,
            confidenceScore: chatUpdates.messages[0]?.confidenceScore,
            isFallbackMessage: chatUpdates.messages[0]?.messageMetadata.isFallbackMessage,
            ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
        },
    });

    chatUpdates.messages.forEach((message) => {
        message.messageMetadata.messageLatency = messageLatency;
    });
    return chatUpdates;
};
